import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Drawer, IconButton, Switch, Typography } from '@mui/material';
import { IconExternalLink } from '@tabler/icons-react';
import { getAiSummary, toggleAiSummaryPreference } from 'api/aiSummaryPreference';
import { AppConfig } from 'AppConfig';
import * as FavoriteReportActions from 'features/frontoffice/favoriteReport/actions/action';
import BookmarkIcon from 'icons/BookmarkIcon';
import GradientSparklesIcon from 'icons/GradientSparklesIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'store/store';
import { hasPermission } from 'utils/common';
import { SET_AI_SUMMARY_PREFERENCE } from 'features/user/userTypes';
import { resolveSmartCardTitle } from './smartCardTitle';

const UserTypes = { SET_AI_SUMMARY_PREFERENCE };

const colors = {
  border: '#71767A',
  line: '#E0E0E0',
  primary: '#2F54A5',
  switchActive: '#001A47',
  heading: '#000000',
  subtitle: '#121A22',
  summaryBorder: '#2E54A6',
  summaryOutlineGradient:
    'linear-gradient(135deg, #C6D41F 0%, #068CF2 45.988%, #2E54A6 100%)',
  summaryBackground:
    'linear-gradient(-90deg, rgba(46, 84, 166, 0.05) 8.0247%, rgba(6, 140, 242, 0.05) 45.988%, rgba(198, 212, 31, 0.05) 100%), linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 100%)'
};

const getCardId = (card) => String(card?.id ?? card?.scId ?? '');

const AI_SUMMARY_PERSONA = 'broker';
const AI_SUMMARY_INFO_TEXT = 'AI-generated insights based on report data. Selected filters are not applied.';
const DESCRIPTION_MAX_LENGTH = 500; // BNIST-12610 AC-3: defensive cap in case a legacy description exceeds the Global Config limit.

const SUMMARY_EMPTY_STATE = {
  unavailable: {
    title: 'Summary Unavailable',
    message: 'A summary has not been generated for this report yet.'
  },
  error: {
    title: 'Summary Unavailable',
    message: 'There is an error generating a summary for this report. Please try again later.'
  },
  disabled: {
    title: 'AI Summary Disabled',
    message: 'Turn on AI Summaries to view generated insights.'
  }
};

const actionButtonSx = {
  minWidth: 'auto',
  padding: 0,
  color: colors.primary,
  fontFamily: 'Noto Sans',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  textTransform: 'none',
  justifyContent: 'flex-start',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#214A99'
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px'
  }
};

const headerTypographySx = {
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: '21px',
  letterSpacing: '0.14px',
  color: colors.heading
};

const summaryStateMessageSx = {
  fontFamily: 'Noto Sans',
  fontSize: 14,
  lineHeight: '21px',
  color: colors.subtitle
};

const aiSummaryInfoTypographySx = {
  fontFamily: 'Noto Sans',
  fontSize: 12,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '140%',
  color: 'var(--Gray-Darkest---Body-Text, #121A22)',
  leadingTrim: 'both',
  textEdge: 'cap'
};


const getSummaryState = (summarySections, summaryLoadError) => {
  if (summaryLoadError) return 'error';

  if (!Array.isArray(summarySections) || summarySections.length === 0) return 'unavailable';

  return null;
};

const toSummaryContent = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === null || typeof value === 'undefined' || value === '') return [];
  return [String(value)];
};

const normalizeSummarySection = (section, index) => {
  if (!section) return null;

  if (typeof section !== 'object') {
    const content = toSummaryContent(section);
    return content.length ? { section: `section-${index + 1}`, heading: null, style: 'paragraph', content } : null;
  }

  const content = toSummaryContent(section.content);
  if (content.length === 0) return null;

  return {
    ...section,
    section: section.section || `section-${index + 1}`,
    heading: section.heading ?? section.title ?? null,
    style: section.style || (content.length > 1 ? 'bullets' : 'paragraph'),
    content
  };
};

const normalizeSummarySections = (summary) => {
  if (!summary) return [];
  if (Array.isArray(summary)) return summary.map(normalizeSummarySection).filter(Boolean);
  if (Array.isArray(summary.sections)) return summary.sections.map(normalizeSummarySection).filter(Boolean);
  return [];
};

const renderTextWithBold = (text) => String(text || '')
  .split(/(\*\*[^*]+\*\*)/g)
  .map((part, index) => {
    const isBold = part.startsWith('**') && part.endsWith('**');
    return isBold ? (
      <Box component="strong" key={`${part}-${index}`} sx={{ fontWeight: 700 }}>
        {part.slice(2, -2)}
      </Box>
    ) : part;
  });

const resolveAiSummaryEnabled = (rawValue, fallbackValue = true) => {
  if (rawValue === undefined || rawValue === null) {
    return fallbackValue;
  }

  if (rawValue === false || rawValue === 'false' || rawValue === 0 || rawValue === '0') {
    return false;
  }

  if (rawValue === true || rawValue === 'true' || rawValue === 1 || rawValue === '1') {
    return true;
  }

  return fallbackValue;
};

const SmartCardQuickViewDrawer = ({ open, onClose, card, onLaunch }) => {
  const dispatch = useDispatch();
  const favoriteReports = useSelector((state) => state.favoriteReport.favoriteReports);
  const permissions = useSelector((state) => state.user.permissions);
  const aiSummaryAvailable = useSelector((state) => state.user.aiSummaryAvailable);
  const userAiSummaryEnabled = useSelector((state) => state.user.aiSummaryEnabled);
  const hasAiSummaryAccess =
    aiSummaryAvailable === true &&
    hasPermission(AppConfig.PERMISSION.FO_AI_SUMMARY_VIEW, permissions);

  const cardId = getCardId(card);
  const isFavorited = Array.isArray(favoriteReports)
    ? favoriteReports.some((report) => getCardId(report) === cardId)
    : !!card?.favorited;

  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(
    resolveAiSummaryEnabled(userAiSummaryEnabled, true)
  );
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [summarySections, setSummarySections] = useState([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryLoadError, setSummaryLoadError] = useState(false);
  const userAiSummaryEnabledRef = useRef(userAiSummaryEnabled);

  useEffect(() => {
    userAiSummaryEnabledRef.current = userAiSummaryEnabled;
  }, [userAiSummaryEnabled]);

  useEffect(() => {
    let isActive = true;

    if (open && card?.id && hasAiSummaryAccess) {
      const resolvedUserPreference = resolveAiSummaryEnabled(userAiSummaryEnabledRef.current, null);

      setSummarySections([]);
      setSummaryLoadError(false);

      if (resolvedUserPreference === false) {
        setAiSummaryEnabled(false);
        setIsSummaryLoading(false);
        return () => {
          isActive = false;
        };
      }

      setAiSummaryEnabled(resolvedUserPreference ?? true);
      setIsSummaryLoading(true);

      getAiSummary(card.id, AI_SUMMARY_PERSONA)
        .then((data) => {
          if (!isActive) return;
          // If the user disabled AI summaries while this request was in-flight, ignore the response.
          if (userAiSummaryEnabledRef.current === false) return;

          const enabled = resolveAiSummaryEnabled(data?.enabled, resolvedUserPreference ?? true);
          setAiSummaryEnabled(enabled);
          dispatch({ type: UserTypes.SET_AI_SUMMARY_PREFERENCE, payload: enabled });
          setSummarySections(enabled ? normalizeSummarySections(data) : []);
        })
        .catch(() => {
          if (!isActive) return;
          if (userAiSummaryEnabledRef.current === false) return;

          setAiSummaryEnabled(resolvedUserPreference ?? true);
          setSummaryLoadError(true);
        })
        .finally(() => {
          if (isActive) setIsSummaryLoading(false);
        });
    } else {
      setSummarySections([]);
      setSummaryLoadError(false);
      setIsSummaryLoading(false);
    }

    return () => {
      isActive = false;
    };
  }, [open, card?.id, hasAiSummaryAccess, dispatch]);

  const handleToggleAiSummary = useCallback(async (event) => {
    const newValue = event.target.checked;
    if (!card?.id || isToggleLoading) return;

    setIsToggleLoading(true);
    setAiSummaryEnabled(newValue);
    try {
      await toggleAiSummaryPreference(newValue);
      dispatch({ type: UserTypes.SET_AI_SUMMARY_PREFERENCE, payload: newValue });
      if (newValue) {
        setSummarySections([]);
        setSummaryLoadError(false);
        setIsSummaryLoading(true);
        try {
          const data = await getAiSummary(card.id, AI_SUMMARY_PERSONA);
          const enabled = resolveAiSummaryEnabled(data?.enabled, true);
          setAiSummaryEnabled(enabled);
          dispatch({ type: UserTypes.SET_AI_SUMMARY_PREFERENCE, payload: enabled });
          setSummarySections(enabled ? normalizeSummarySections(data) : []);
        } catch (err) {
          setAiSummaryEnabled(true);
          setSummaryLoadError(true);
          console.error('Error loading AI Summary:', err);
        } finally {
          setIsSummaryLoading(false);
        }
      } else {
        setSummarySections([]);
        setSummaryLoadError(false);
      }
    } catch (err) {
      setAiSummaryEnabled(!newValue);
      console.error('Error toggling AI Summary preference:', err);
    } finally {
      setIsToggleLoading(false);
    }
  }, [card?.id, isToggleLoading, dispatch]);

  const title = resolveSmartCardTitle(card);
  const rawDescription = card?.description ? String(card.description).trim() : '';
  const hasDescription = rawDescription.length > 0;
  const description = hasDescription
    ? (rawDescription.length > DESCRIPTION_MAX_LENGTH
        ? `${rawDescription.slice(0, DESCRIPTION_MAX_LENGTH - 1)}…`
        : rawDescription)
    : 'No description available.';
  const cardSummaryState = isSummaryLoading ? null : getSummaryState(summarySections, summaryLoadError);
  const isSummaryUnavailable = cardSummaryState === 'unavailable' || cardSummaryState === 'error';
  const summaryState = aiSummaryEnabled === false ? 'disabled' : (isSummaryUnavailable ? cardSummaryState : null);
  const summaryEmptyState = summaryState ? SUMMARY_EMPTY_STATE[summaryState] : null;

  const toggleFavorite = useCallback(async () => {
    if (!card?.id) return;

    try {
      const action = isFavorited
        ? FavoriteReportActions.deleteFavoriteReport(card.id, card.category, false)
        : FavoriteReportActions.addFavoriteReport(card.id, card.category, false);

      await store.dispatch(action);
      await store.dispatch(FavoriteReportActions.getFavoriteReport());
    } catch (err) {
      const statusCode = err?.response?.status ?? err?.status;
      console.error('Error toggling favorite:', statusCode ? `HTTP ${statusCode}` : err);
    }
  }, [card, isFavorited]);

  const handleLaunch = useCallback(() => {
    onClose?.();
    onLaunch?.(card);
  }, [card, onClose, onLaunch]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 672 },
          maxWidth: '100%',
          boxShadow: '-12px 0px 28px rgba(17, 17, 17, 0.08)',
          borderRadius: 0,
          backgroundColor: '#FFFFFF'
        }
      }}
    >
      <Box display="flex" flexDirection="column" height="100vh" bgcolor="#FFFFFF">
        <Box px={{ xs: 2, sm: 6, md: 3 }} py={3} borderBottom={`1px solid ${colors.border}`}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <IconButton
              aria-label="Close quick view"
              onClick={onClose}
              edge="end"
              sx={{
                color: '#1F1F1F',
                '&:hover svg': { color: '#000000' }
              }}
            >
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Box>

        <Box flex={1} overflow="auto" px={{ xs: 2, sm: 6, md: 3 }} pt={3} pb={4}>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontWeight: 600,
              fontSize: 20,
              lineHeight: '30px',
              color: colors.heading,
              mb: 2
            }}
          >
            {title}
          </Typography>

          <Box display="flex" alignItems="center" gap={2.5} mb={3}>
            <Button
              onClick={toggleFavorite}
              startIcon={<BookmarkIcon filled={isFavorited} size={20} color={colors.primary} />}
              sx={actionButtonSx}
              aria-label={isFavorited ? 'unfavorite card' : 'favorite card'}
            >
              {isFavorited ? 'Unsave' : 'Save'}
            </Button>
          </Box>

          <Box mb={3}>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '21px',
                letterSpacing: '0.14px',
                color: colors.heading,
                mb: 1
              }}
            >
              Description
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Noto Sans',
                fontSize: 14,
                lineHeight: '21px',
                color: colors.subtitle
              }}
            >
              {description}
            </Typography>
          </Box>

          {hasAiSummaryAccess && (
          <Box
            sx={{
              border: summaryEmptyState ? 'none' : '1px solid transparent',
              borderRadius: '8px',
              background: summaryEmptyState
                ? '#F8F8F8'
                : `${colors.summaryBackground} padding-box, ${colors.summaryOutlineGradient} border-box`,
              px: 3,
              py: 3
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
              {isSummaryUnavailable && aiSummaryEnabled ? (
                <Typography sx={headerTypographySx}>
                  {SUMMARY_EMPTY_STATE[cardSummaryState].title}
                </Typography>
              ) : aiSummaryEnabled ? (
                  <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
                    <GradientSparklesIcon size={20} />
                    <Box display="flex" flexDirection="column" gap="2px" minWidth={0}>
                      <Typography sx={headerTypographySx}>
                        Summary
                      </Typography>
                      <Typography sx={aiSummaryInfoTypographySx}>
                        {AI_SUMMARY_INFO_TEXT}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={headerTypographySx}>
                    AI Summary Disabled
                  </Typography>
                )}
              <Switch
                checked={aiSummaryEnabled === true}
                onChange={handleToggleAiSummary}
                disabled={isToggleLoading}
                inputProps={{ 'aria-label': 'Toggle AI Summary' }}
                sx={{
                  width: 40,
                  height: 24,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: '3px',
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.switchActive,
                        opacity: 1
                      }
                    }
                  },
                  '& .MuiSwitch-thumb': {
                    width: 18,
                    height: 18,
                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.10)'
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: '12px',
                    backgroundColor: '#71767A',
                    opacity: 1
                  }
                }}
              />
            </Box>

            {isSummaryLoading && aiSummaryEnabled ? (
                <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                  <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
            ) : summaryState === 'disabled' ? (
                <Typography
                  sx={summaryStateMessageSx}>
                  {SUMMARY_EMPTY_STATE.disabled.message}
                </Typography>
            ) : summaryState ? (
                <Typography
                  sx={summaryStateMessageSx}>
                  {summaryEmptyState.message}
                </Typography>
            ) : (
                summarySections.map((section, index) => (
                  <Box key={section.section || section.heading || index} mb={index === summarySections.length - 1 ? 0 : 2.5}>
                    {section.heading ? (
                      <Typography
                        sx={{
                          fontFamily: 'Noto Sans',
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: '21px',
                          color: colors.heading,
                          mb: 0.5
                        }}
                      >
                        {section.heading}
                      </Typography>
                    ) : null}
                    {section.style === 'bullets' ? (
                      <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                        {(section.content || []).map((item, itemIndex) => (
                          <Typography
                            component="li"
                            key={`${section.section || section.heading}-${itemIndex}`}
                            sx={{
                              fontFamily: 'Noto Sans',
                              fontSize: 14,
                              lineHeight: '21px',
                              color: colors.subtitle,
                              mb: itemIndex === (section.content || []).length - 1 ? 0 : 0.75
                            }}
                          >
                            {renderTextWithBold(item)}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      (section.content || []).map((item, itemIndex) => (
                        <Typography
                          key={`${section.section || section.heading}-${itemIndex}`}
                          sx={{
                            fontFamily: 'Noto Sans',
                            fontSize: 14,
                            lineHeight: '21px',
                            color: colors.subtitle,
                            mb: itemIndex === (section.content || []).length - 1 ? 0 : 1
                          }}
                        >
                          {renderTextWithBold(item)}
                        </Typography>
                      ))
                    )}
                  </Box>
                ))
            )}
          </Box>
          )}
        </Box>

        <Box borderTop={`1px solid ${colors.border}`} px={{ xs: 2, sm: 6, md: 2 }} py={3} bgcolor="#FFFFFF">
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleLaunch}
              sx={{
                borderRadius: '24px',
                textTransform: 'none',
                fontFamily: 'Noto Sans',
                fontWeight: 600,
                fontSize: 16,
                height: 44,
                px: 2,
                py: 0,
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: colors.primary,
                '&:hover': { backgroundColor: '#214A99' },
                '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 }
              }}
              startIcon={<IconExternalLink size={20} color="#FFFFFF" />}
            >
              Launch
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SmartCardQuickViewDrawer;